import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShoppingCart, Menu, X, Phone, Mail, ChevronRight, Wrench, Truck } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { CONTACT_INFO, categories } from '../../data/mock';
import Logo from '../Logo';

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { cartCount, openCart } = useCart();
  const location = useLocation();

  useEffect(() => { setIsMobileMenuOpen(false); }, [location]);

  const phoneHref = `tel:${CONTACT_INFO.phone.replace(/[^0-9]/g, '')}`;

  const utilityLinks = [
    { name: 'Home', path: '/' },
    { name: 'All Tools', path: '/shop' },
    { name: 'Support', path: '/faq' },
    { name: 'Contact', path: '/contact' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header className="sticky top-0 left-0 right-0 z-50">
      {/* Top ticker bar */}
      <div className="bg-athletic-600 text-white text-[11px] font-bold uppercase tracking-widest">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-8 flex items-center justify-between gap-4">
          <p className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> Free USPS shipping on every US order</p>
          <a href={phoneHref} className="hidden sm:flex items-center gap-1.5 hover:text-dark-900 transition-colors"><Phone className="w-3.5 h-3.5" /> {CONTACT_INFO.phone}</a>
        </div>
      </div>

      {/* Main row */}
      <div className="bg-white border-b border-dark-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-[74px] gap-4">
            <Logo />

            {/* Utility nav (desktop, right) */}
            <div className="hidden lg:flex items-center gap-7">
              {utilityLinks.map((link) => (
                <Link key={link.path} to={link.path} className={`text-sm font-semibold uppercase tracking-wide transition-colors ${isActive(link.path) ? 'text-athletic-600' : 'text-dark-700 hover:text-athletic-600'}`}>{link.name}</Link>
              ))}
              <a href={`mailto:${CONTACT_INFO.email}`} className="flex items-center gap-1.5 text-sm font-medium text-dark-500 hover:text-athletic-600 transition-colors"><Mail className="w-4 h-4" /></a>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/shop" className="hidden xl:inline-flex items-center gap-2 px-5 py-2.5 rounded-sm bg-dark-900 text-white text-sm font-bold uppercase tracking-wide hover:bg-athletic-600 transition-colors">
                <Wrench className="w-4 h-4" /> Shop Tools
              </Link>
              <button
                onClick={openCart}
                data-testid="cart-button"
                className="relative w-11 h-11 rounded-sm flex items-center justify-center text-white bg-athletic-600 hover:bg-athletic-700 transition-colors"
                aria-label="Cart"
              >
                <ShoppingCart className="w-5 h-5" />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-dark-900 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center border-2 border-white">
                    {cartCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="lg:hidden w-11 h-11 rounded-sm flex items-center justify-center text-dark-900 bg-dark-100 border border-dark-200 hover:bg-dark-200"
                aria-label="Menu"
              >
                {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Category strip (desktop) */}
      <div className="hidden lg:block bg-dark-900 text-dark-200 border-b-2 border-athletic-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1 h-12">
            <Link to="/shop" className="flex items-center gap-2 px-4 h-full text-xs font-bold uppercase tracking-widest bg-athletic-600 text-white hover:bg-athletic-700 transition-colors">
              <Menu className="w-4 h-4" /> All Categories
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.id}
                to={`/shop?category=${cat.slug}`}
                className="px-4 h-full flex items-center text-xs font-semibold uppercase tracking-wide text-dark-200 hover:text-white hover:bg-dark-800 transition-colors"
              >
                {cat.name}
              </Link>
            ))}
            <span className="ml-auto text-[11px] font-medium tracking-wide text-dark-400 flex items-center gap-1.5">
              <Truck className="w-3.5 h-3.5 text-athletic-500" /> 2&ndash;4 day delivery &middot; 30-day returns
            </span>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden border-b border-dark-200 bg-white shadow-elevated">
          <div className="max-w-7xl mx-auto px-4 py-3 space-y-1">
            {utilityLinks.map((link) => (
              <Link key={link.path} to={link.path} className="flex items-center justify-between px-4 py-3 text-base font-bold uppercase tracking-wide text-dark-800 hover:bg-athletic-50 hover:text-athletic-600 rounded-sm">{link.name}<ChevronRight className="w-4 h-4 text-dark-300" /></Link>
            ))}
            <div className="pt-2 mt-2 border-t border-dark-100">
              <p className="px-4 py-2 text-[11px] font-bold uppercase tracking-widest text-dark-400">Categories</p>
              {categories.map((cat) => (
                <Link key={cat.id} to={`/shop?category=${cat.slug}`} className="flex items-center gap-3 px-4 py-2.5 text-sm font-semibold text-dark-600 hover:bg-athletic-50 hover:text-athletic-600 rounded-sm">
                  <span className="w-9 h-9 rounded-sm overflow-hidden bg-dark-100 flex-shrink-0"><img src={cat.image} alt="" className="w-full h-full object-cover" /></span>
                  {cat.name}
                </Link>
              ))}
            </div>
            <a href={phoneHref} className="flex items-center gap-2 mt-3 px-4 py-3 rounded-sm bg-athletic-600 text-white font-bold uppercase tracking-wide justify-center"><Phone className="w-4 h-4" /> Call {CONTACT_INFO.phone}</a>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
